// ThemeMaker.tsx
import Sidebar from "./sidebar";
import { useEffect, useMemo, useState, useContext } from "react";
import style from "./thememaker.module.css";
import { ReactNode } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { MdDashboard } from "react-icons/md";
import { useSidebar } from "../../context/SidebarContext";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
  MdPerson,
  MdDesignServices,
  MdRoomPreferences,
  MdCategory,
  MdSettings,
  MdOutlinePets,
  MdPets,
  MdExtension,
  MdPeople,
  MdInventory2,
  MdLocalShipping,
  MdAssignment,
  MdShoppingCart,
  MdOutlinePayments,
  MdDirectionsWalk,
  MdLocalHospital,
  MdLocalOffer,
  MdFeedback,
  MdNotificationsNone,
  MdAssignmentTurnedIn,
} from "react-icons/md";
import { BiLineChartDown } from "react-icons/bi";
import { HiOutlineClipboardList } from "react-icons/hi";
import { BsHouseGearFill } from "react-icons/bs";
import { BsShopWindow } from "react-icons/bs";
import { TokenContext } from "../../context/TokenContext";
import { FaBuildingShield } from "react-icons/fa6";
import { useProfile } from "../../context/ProfileContext";
import { FaCalendarAlt, FaCalendarCheck, FaClipboardList } from "react-icons/fa";
// import Breadcrumbs, { getLabelForSegment } from "../Breadcrumbs";
import Breadcrumbs from "../Breadcrumbs";
import { TbFileInvoice } from "react-icons/tb";
import { useSocket } from "../../hooks/useSocket";
import { IoBagCheckOutline } from "react-icons/io5";

interface Props {
  children?: ReactNode;
  noPageScroll?: boolean; // NEW
}

const ThemeMaker = ({ children, noPageScroll = false }: Props) => {
  const { showSidebar, setShowSidebar } = useSidebar();
  const navigate = useNavigate();
  const [shouldRedirectToBookings, setShouldRedirectToBookings] =
    useState(false);
  const location = useLocation();

  const [isDesktopScreen, setIsDesktopScreen] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1280 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopScreen(window.innerWidth >= 1280);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1280) {
      setShowSidebar(false);
    }
  }, [location, setShowSidebar]);

  const handleAsideClick = (e: React.MouseEvent<HTMLElement>) => {
    if (typeof window !== "undefined" && window.innerWidth >= 1280) return;
    const target = e.target as HTMLElement;
    if (target.closest("a")) {
      setShowSidebar(false);
    }
  };

  // subscription / token context
  const { decodedData, isFreePlan, isHomePlan, isShopPlan, isEmployee } =
    useContext(TokenContext);
  const data = decodedData?.data || {};
  useSocket(); // connects the socket once when layout mounts

  // get services and helpers from ProfileContext
  const {
    services = [],
    fetchProfile,
    menuPermissions = [],
    loadingProfile,
    profileData,
    rawServiceCategory = [],
  } = useProfile();

  // Fix the executiveMenus list - should be 4 menus, not 5
  const isMenuAllowedByName = (menuName: string): boolean => {
    if (!menuPermissions || menuPermissions.length === 0) return true;
    //  const notYetInDb = ["Forms", "Online Requests"];
    //  if (notYetInDb.includes(menuName)) return true;
    const notYetInDb = ["Boarding Activity"];
    if (notYetInDb.includes(menuName)) return true;

    const permission = menuPermissions.find(
      (p: any) => p.menu_name === menuName,
    );

    // NOT IN DB — deny
    if (!permission) return false;

    const userRole = data?.user?.role_data?.name?.toLowerCase();
    if (permission.is_enabled === false) return false;
    return profileData.allowedMenus.includes(permission.menu_id);

    if (typeof permission.admin === "boolean") {
      // NEW FORMAT
      if (userRole === "admin") return permission.admin === true;
      if (userRole === "manager") return permission.manager === true;
      if (userRole === "executive") return permission.executive === true;
      return false;
    }

    if (!permission.is_active) return false;
    const rolePermission = permission.role_permissions?.find(
      (rp: any) => rp.role_name?.toLowerCase() === userRole,
    );
    if (!rolePermission) return userRole === "admin" || userRole === "manager";
    return rolePermission.is_active;
  };

  // small local loading state for UI guard (optional)
  const [loadingLocal, setLoadingLocal] = useState(false);
  // MANAGER DASHBOARD PERMISSION CHECK
  useEffect(() => {
    if (!menuPermissions?.length || !data?.user?.role_data) return;

    const userRole = data.user.role_data.name?.toLowerCase();
    if (userRole !== "manager") return; // Only apply logic for Manager

    const dashboardPermission = menuPermissions.find(
      (p: any) => p.menu_name === "Dashboard",
    );

    const isDashboardActiveForManager =
      dashboardPermission?.role_permissions?.some(
        (rp: any) =>
          rp.role_name?.toLowerCase() === "manager" && rp.is_active === true,
      ) ?? false;

    if (!isDashboardActiveForManager) {
      setShouldRedirectToBookings(true);
    }
  }, [menuPermissions, data?.user?.role_data]);

  useEffect(() => {
    if (shouldRedirectToBookings) {
      navigate("/admin/booking-management/listing", { replace: true });
    }
  }, [shouldRedirectToBookings, navigate]);

  const [openMenu, setOpenMenu] = useState<{
    main: string | null;
    sub: string | null;
  }>({
    main: null,
    sub: null,
  });

  // ensure we have services on mount (only fetch if services empty)
  useEffect(() => {
    let cancelled = false;
    const ensure = async () => {
      if (!services || services.length === 0) {
        try {
          setLoadingLocal(true);
          await fetchProfile?.();
        } catch (err) {
          console.error("ThemeMaker: failed to fetch profile", err);
        } finally {
          if (!cancelled) setLoadingLocal(false);
        }
      }
    };
    ensure();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Optional: listen to global updates if something updates services directly on server
  useEffect(() => {
    const handler = () => {
      // If other components update backend directly and dispatch an event,
      // re-fetch the profile so we stay in sync.
      fetchProfile?.();
    };
    window.addEventListener("servicesUpdated", handler);
    return () => window.removeEventListener("servicesUpdated", handler);
  }, [fetchProfile]);

  // Memoized boolean checks (cheap on re-renders)
  const groomingActive = useMemo(() => {
    const tokenCats = data?.vendor?.service_category || [];

    return (
      services.some(
        (s: any) =>
          (s.category_name === "Grooming" ||
            s.category_name === "Home Grooming" ||
            s.service_category_name === "Grooming" ||
            s.service_category_name === "Home Grooming") &&
          (s.isActive === 1 || s.isActive === true),
      ) ||
      tokenCats.some(
        (c: any) =>
          c.service_category_name === "Grooming" ||
          (c.service_category_name === "Home Grooming" &&
            (c.isActive === 1 || c.isActive === true)),
      )
    );
  }, [services, data]);

  const boardingActive = useMemo(() => {
    const tokenCats = data?.vendor?.service_category || [];
    return (
      services.some(
        (s: any) =>
          (s.category_name === "Boarding" ||
            s.service_category_name === "Boarding" ||
            s.service_category_name === "Kennel Boarding") &&
          (s.isActive === 1 || s.isActive === true),
      ) ||
      tokenCats.some(
        (c: any) =>
          (c.service_category_name === "Boarding" ||
            c.service_category_name === "Kennel Boarding") &&
          (c.isActive === 1 || c.isActive === true),
      )
    );
  }, [services, data]);

  const vetActive = useMemo(() => {
    const tokenCats = data?.vendor?.service_category || [];
    return (
      services.some(
        (s: any) =>
          (s.category_name === "Vet Clinic" ||
            s.service_category_name === "Vet Clinic" ||
            s.service_category_name === "Pet Vet Clinic") &&
          (s.isActive === 1 || s.isActive === true),
      ) ||
      tokenCats.some(
        (c: any) =>
          (c.service_category_name === "Vet Clinic" ||
            c.service_category_name === "Pet Vet Clinic") &&
          (c.isActive === 1 || c.isActive === true),
      )
    );
  }, [services, data]);

  const dayCareActive = useMemo(
    () =>
      services.some(
        (s: any) => s.category_name === "Day Care" && s.isActive === 1,
      ),
    [services],
  );

  const dogWalkingActive = useMemo(
    () =>
      services.some(
        (s: any) => s.category_name === "Dog Walks" && s.isActive === 1,
      ),
    [services],
  );

  const petStoreActive = useMemo(() => {
    // Check services array from ProfileContext
    if (services.length > 0) {
      return services.some(
        (s: any) =>
          (s.category_name === "Pet Store" ||
            s.category_name === "Pet Shop" ||
            s.service_category_name === "Pet Store" ||
            s.service_category_name === "Pet Shop") &&
          (s.isActive === 1 || s.isActive === true),
      );
    }

    // Fallback: check service_category directly from token (this exists!)
    const tokenServiceCategories = data?.vendor?.service_category || [];
    if (tokenServiceCategories.length > 0) {
      return tokenServiceCategories.some(
        (c: any) =>
          (c.service_category_name === "Pet Shop" ||
            c.service_category_name === "Pet Store") &&
          (c.isActive === 1 || c.isActive === true),
      );
    }

    // Last fallback: plan_category
    const planCategories =
      data?.vendor?.subscriptionPlan?.[0]?.plan_category || [];
    return planCategories.some(
      (c: any) =>
        c.service_category_name === "Pet Shop" ||
        c.service_category_name === "Pet Store",
    );
  }, [services, data]);

  const isShopOnly = useMemo(() => {
    if (!rawServiceCategory || rawServiceCategory.length === 0) return false;
    const isPetShopOrPosCategory = (cat: any) => {
      const name = cat.service_category_name?.toLowerCase() || "";
      return name === "pet shop" || name === "pos";
    };
    const nonShop = rawServiceCategory.filter(
      (cat: any) => !isPetShopOrPosCategory(cat),
    );
    return nonShop.length === 0;
  }, [rawServiceCategory]);

  // Toggle menu helpers
  const toggleMainMenu = (menuName: string) => {
    setOpenMenu((prev) => ({
      main: prev.main === menuName ? null : menuName,
      sub: null,
    }));
  };

  const toggleSubMenu = (subMenuName: string) => {
    setOpenMenu((prev) => ({
      ...prev,
      sub: prev.sub === subMenuName ? null : subMenuName,
    }));
  };

  useEffect(() => {
    const path = location.pathname;
    let mainMenu: string | null = null;
    let subMenu: string | null = null;

    // Determine main menu based on route
    if (
      [
        "/admin/service-management/grooming",
        "/admin/service-management/addon_service",
        "/admin/service-management/boarding",
        "/admin/service-management/dog_walking",
        "/admin/service-management/vet_clinic",
        "/admin/service-management/daycare",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "serviceManagement";
    } else if (
      [
        "/admin/inventory-management/purchase_bills",
        "/admin/inventory-management/purchase_bills/new",
        // "/admin/inventory-management/purchase_bills",
        "/admin/inventory-management/products",
        "/admin/inventory-management/suppliers",
        "/admin/inventory-management/suppliers/return",
        "/admin/inventory-management/assigned-inventory",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "inventory";
    } else if (
      path.startsWith("/admin/inventory-management/assigned-inventory")
    ) {
      mainMenu = "inventory";
    } else if (
      [
        "/admin/settings/account-setup",
        "/admin/settings/service_amenities",
        "/admin/room_management",
        "/admin/settings/size_management",
        "/admin/settings/coat_management",
        "/admin/settings/promotions_and_coupons",
        "/admin/settings/store_management",
        "/admin/settings/general_settings",
        "/admin/settings/online_profile",
        "/admin/discounts",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "settings";
    } else if (
      [
        "/admin/booking-management/listing",
        "/admin/booking-management/calendar",
        "/admin/booking-management/online-appointments",
        "/admin/booking-management/checkout-bookings",
        "/admin/booking-management/grooming-bookings",
        "/admin/booking-management/boarding-bookings",
        "/admin/booking-management/vet-bookings",
        "/pos",
        "/admin/order-management/enquiry",
        //"/admin/booking-management/boarding-activity"
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "bookingManagement";
    } else if (
      [
        "/admin/order-management/orders",
        "/invoice",
        "/admin/order-management/payments",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "orderManagement";
    } else if (
      [
        "/admin/expense-management/expenses",
        "/admin/expense-management/payments",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "expenseManagement";
    } else if (
      [
        "/admin/reports/standard",
        "/admin/reports/custom",
        "/admin/reports/feedback",
        "/admin/reports/reminderhistory",
      ].some((route) => path.startsWith(route))
    ) {
      mainMenu = "reports";
    }
    setOpenMenu({ main: mainMenu, sub: subMenu });
  }, [location.pathname]);

  // derive pageTitle from current path (last segment) using same label helper
  // const pageTitle = useMemo(() => {
  //   const parts = location.pathname.split("/").filter((p) => p !== "");
  //   if (parts.length === 0) return "Dashboard";
  //   const last = parts[parts.length - 1];
  //   return getLabelForSegment(last);
  // }, [location.pathname]);
  // render as before — using the context-powered booleans and menu permissions
  const getNavLinkClass = (isActive: boolean, isSub: boolean = false) => {
    if (isSub) {
      return isActive
        ? "relative flex items-center gap-2 px-2.5 py-2 rounded-r-xl rounded-l-xs bg-[#1B4582]/10 text-[#1B4582] font-semibold text-[13.5px] transition-all duration-200 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:bg-[#1B4582] before:rounded-r-full shadow-xs whitespace-nowrap"
        : "relative flex items-center gap-2 px-2.5 py-2 rounded-lg text-gray-600 font-medium text-[13.5px] hover:text-[#1B4582] hover:bg-[#1B4582]/10 transition-all duration-200 ease-in-out hover:translate-x-1 group whitespace-nowrap";
    }
    return isActive
      ? "relative flex items-center gap-2.5 px-3 py-2.5 rounded-r-xl rounded-l-md bg-[#1B4582]/10 text-[#1B4582] font-semibold text-sm transition-all duration-200 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:bg-[#1B4582] before:rounded-r-full shadow-xs whitespace-nowrap"
      : "relative flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-gray-600 font-medium text-sm hover:text-[#1B4582] hover:bg-[#1B4582]/10 transition-all duration-200 ease-in-out hover:translate-x-1 group whitespace-nowrap";
  };

  const getParentBtnClass = (isOpen: boolean) => {
    return isOpen
      ? "w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#1B4582] bg-[#1B4582]/10 transition-all duration-200 ease-in-out group whitespace-nowrap"
      : "w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:text-[#1B4582] hover:bg-[#1B4582]/10 transition-all duration-200 ease-in-out hover:translate-x-1 group whitespace-nowrap";
  };

  return (
    <div className={style.bigScreenSidebar}>
      <div>
        <Sidebar />
        <div id="content-wrapper" className="d-flex flex-column">
          <div
            id="content"
            className="flex h-screen w-[100%]"
            style={
              {
                paddingTop: "var(--top-navbar-height, 70px)",
                // Expose sidebar width to descendants so they can align correctly.
                ["--left-nav-width" as any]:
                  !isEmployee && isDesktopScreen ? "max(16vw, 240px)" : "0px",
              } as React.CSSProperties
            }
          >
            {/* Mobile/Tablet Backdrop Overlay with smooth fade */}
            {!isEmployee && (
              <div
                style={{ top: "var(--top-navbar-height, 70px)" }}
                className={`fixed inset-0 bg-black/50 backdrop-blur-xs z-[99999] xl:hidden transition-opacity duration-300 ease-in-out ${
                  showSidebar
                    ? "opacity-100 pointer-events-auto"
                    : "opacity-0 pointer-events-none"
                }`}
                onClick={() => setShowSidebar(false)}
              />
            )}
            {/* Sidebar */}
            {!isEmployee && (
              <aside
                onClick={handleAsideClick}
                className={`fixed left-0 bottom-0 py-4 px-2 overflow-y-auto bg-white text-gray-700 border-r border-gray-200/80 shadow-2xl xl:shadow-xs select-none z-[100000] xl:z-30 w-[260px] max-w-[80vw] xl:w-[max(16vw,240px)] transition-all duration-300 ease-in-out ${
                  showSidebar
                    ? "translate-x-0 opacity-100 pointer-events-auto"
                    : "-translate-x-full opacity-0 pointer-events-none xl:translate-x-0 xl:opacity-100 xl:pointer-events-auto"
                }`}
                style={{
                  top: "var(--top-navbar-height, 70px)",
                  scrollbarWidth: "thin",
                }}
              >
                <ul className="space-y-1.5 text-sm">
                  {/* Dashboard - Hidden for Manager if permission is false */}
                  {isMenuAllowedByName("Dashboard") &&
                    !(
                      data?.user?.role_data?.name?.toLowerCase() ===
                        "manager" && shouldRedirectToBookings
                    ) && (
                      <li>
                        <NavLink
                          to="/dashboard"
                          className={({ isActive }) =>
                            getNavLinkClass(isActive, false)
                          }
                        >
                          <MdDashboard className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          <span>Dashboard</span>
                        </NavLink>
                      </li>
                    )}

                  {/* Booking Management */}
                  {isMenuAllowedByName("Booking Management") && (
                    <li>
                      <button
                        onClick={() => toggleMainMenu("bookingManagement")}
                        className={getParentBtnClass(
                          openMenu.main === "bookingManagement",
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <FaCalendarCheck className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          Booking Management
                        </span>
                        <span>
                          {openMenu.main === "bookingManagement" ? (
                            <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                          ) : (
                            <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                          )}
                        </span>
                      </button>

                      {openMenu.main === "bookingManagement" && (
                        <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                          {/* Online Requests - NEW */}
                          {isMenuAllowedByName("Online Requests") && (
                            <NavLink
                              to="/admin/booking-management/online-appointments"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <FaCalendarAlt className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Online Requests</span>
                            </NavLink>
                          )}
                          {/* Checkout Bookings */}
                          {isMenuAllowedByName("Checkout Bookings") && (
                            <NavLink
                              to="/admin/booking-management/checkout-bookings"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <IoBagCheckOutline className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Checkout Bookings</span>
                            </NavLink>
                          )}
                          {/* Listing Bookings */}
                          {/* {isMenuAllowedByName("Listing Bookings") && (
                            <NavLink
                              to="/admin/booking-management/listing"
                              className={({ isActive }) => getNavLinkClass(isActive, true)}
                            >
                              <HiOutlineClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Listing Bookings</span>
                            </NavLink>
                          )} */}

                          {/* Calendar Bookings */}
                          {/* {isMenuAllowedByName("Calendar Bookings") && (
                            <NavLink
                              to="/admin/booking-management/calendar"
                              className={({ isActive }) => getNavLinkClass(isActive, true)}
                            >
                              <FaCalendarAlt className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Calendar Bookings</span>
                            </NavLink>
                          )} */}
                          {/* {isMenuAllowedByName("Listing Bookings") && (
                            <ul className="ml-4 mt-1 space-y-2">
                              <li>
                                <NavLink
                                  to="/admin/booking-management/listing"
                                  className={({ isActive }) =>
                                    `flex items-center px-3 py-2 gap-2 rounded transition ${
                                      isActive
                                        ? "bg-gray-700"
                                        : "hover:bg-gray-700"
                                    }`
                                  }
                                >
                                  <HiOutlineClipboardList className="text-lg" />
                                  Listing Bookings
                                </NavLink>
                              </li>
                            </ul>
                          )} */}

                          {/* Calendar Bookings */}
                          {/* {isMenuAllowedByName("Calendar Bookings") && (
                            <ul className="ml-4 mt-1 space-y-2">
                              <li>
                                <NavLink
                                  to="/admin/booking-management/calendar"
                                  className={({ isActive }) =>
                                    `flex items-center px-3 py-2 gap-2 rounded transition ${
                                      isActive
                                        ? "bg-gray-700"
                                        : "hover:bg-gray-700"
                                    }`
                                  }
                                >
                                  <FaCalendarAlt className="text-lg" />
                                  Calendar Bookings
                                </NavLink>
                              </li>
                            </ul>
                          )} */}

                          {/* Today's Boarding Activity */}
                          {/* {isMenuAllowedByName("Boarding Activity") && boardingActive && (
                            <NavLink
                              to="/admin/booking-management/boarding-activity"
                              className={({ isActive }) => getNavLinkClass(isActive, true)}
                            >
                              <MdAssignmentTurnedIn className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Daily Care Task</span>
                            </NavLink>
                          )} */}

                          {isMenuAllowedByName("Grooming Bookings") && (
                            <NavLink
                              to="/admin/booking-management/grooming-bookings"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <FaClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Grooming Bookings</span>
                            </NavLink>
                          )}

                          {isMenuAllowedByName("Boarding Bookings") && (
                            <NavLink
                              to="/admin/booking-management/boarding-bookings"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <FaClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Boarding Bookings</span>
                            </NavLink>
                          )}

                          {isMenuAllowedByName("Vet Bookings") && (
                            <NavLink
                              to="/admin/booking-management/vet-bookings"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <FaClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Vet Bookings</span>
                            </NavLink>
                          )}

                          {isMenuAllowedByName("POS Sale") && (
                            <NavLink
                              to="/pos"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <BsShopWindow className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>POS Sale</span>
                            </NavLink>
                          )}

                          {/* Enquiry */}
                          {isMenuAllowedByName("Enquiry") && (
                            <NavLink
                              to="/admin/order-management/enquiry"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <HiOutlineClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Enquiry</span>
                            </NavLink>
                          )}
                        </div>
                      )}
                    </li>
                  )}

                  {/* Daily Care Task */}
                  {isMenuAllowedByName("Daily Care Task") && boardingActive && (
                    <li>
                      <NavLink
                        to="/admin/booking-management/boarding-activity"
                        className={({ isActive }) =>
                          getNavLinkClass(isActive, false)
                        }
                      >
                        <MdAssignmentTurnedIn className="text-lg transition-transform duration-200 group-hover:scale-110" />
                        <span>Daily Care Task</span>
                      </NavLink>
                    </li>
                  )}

                  {/* Service Management */}
                  {isMenuAllowedByName("Service Management") && (
                    <li>
                      <button
                        onClick={() => toggleMainMenu("serviceManagement")}
                        className={getParentBtnClass(
                          openMenu.main === "serviceManagement",
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <MdDesignServices className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          Service Management
                        </span>
                        <span>
                          {openMenu.main === "serviceManagement" ? (
                            <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                          ) : (
                            <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                          )}
                        </span>
                      </button>

                      {openMenu.main === "serviceManagement" && (
                        <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                          {/* Add-On Service */}
                          {isMenuAllowedByName("Add-On Service") && (
                            <NavLink
                              to="/admin/service-management/addon_service"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdExtension className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Add-On Service</span>
                            </NavLink>
                          )}

                          {/* Grooming Services */}
                          {isMenuAllowedByName("Grooming Services") &&
                            groomingActive && (
                              <NavLink
                                to="/admin/service-management/grooming"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdOutlinePets className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Grooming Services</span>
                              </NavLink>
                            )}

                          {/* Boarding Services */}
                          {isMenuAllowedByName("Boarding Services") &&
                            boardingActive && (
                              <NavLink
                                to="/admin/service-management/boarding"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdRoomPreferences className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Boarding Services</span>
                              </NavLink>
                            )}

                          {/* Dog Walking Services */}
                          {isMenuAllowedByName("Dog Walking Services") &&
                            // !isShopPlan &&
                            dogWalkingActive && (
                              <NavLink
                                to="/admin/service-management/dog_walking"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdDirectionsWalk className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Dog Walking Services</span>
                              </NavLink>
                            )}

                          {/* Vet Clinic Services */}
                          {isMenuAllowedByName("Vet Clinic Services") &&
                            // !isFreePlan &&
                            // !isHomePlan &&
                            vetActive && (
                              <NavLink
                                to="/admin/service-management/vet_clinic"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdLocalHospital className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Vet Clinic Services</span>
                              </NavLink>
                            )}

                          {/* Day Care Services */}
                          {/* {isMenuAllowedByName("Day Care Services") &&
                            !isFreePlan &&
                            !isHomePlan &&
                            dayCareActive && (
                              <ul className="ml-4 mt-1 space-y-2">
                                <li>
                                  <NavLink
                                    to="/admin/service-management/daycare"
                                    className={({ isActive }) =>
                                      `flex items-center px-3 py-2 gap-2 rounded transition ${
                                        isActive
                                          ? "bg-gray-700"
                                          : "hover:bg-gray-700"
                                      }`
                                    }
                                  >
                                    <FaBuildingShield className="text-lg" />
                                    Day Care Services
                                  </NavLink>
                                </li>
                              </ul>
                            )} */}
                        </div>
                      )}
                    </li>
                  )}

                  {/* Inventory Management - hidden for free subscription */}
                  {isMenuAllowedByName("Inventory Management") && (
                    // !isFreePlan &&
                    // !isHomePlan &&
                    <li>
                      <button
                        onClick={() => toggleMainMenu("inventory")}
                        className={getParentBtnClass(
                          openMenu.main === "inventory",
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <MdInventory2 className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          Inventory Management
                        </span>
                        <span>
                          {openMenu.main === "inventory" ? (
                            <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                          ) : (
                            <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                          )}
                        </span>
                      </button>
                      {openMenu.main === "inventory" && (
                        <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                          {/* Purchase Bill */}
                          {isMenuAllowedByName("Purchase Bill") && (
                            <NavLink
                              to="/admin/inventory-management/purchase_bills"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdAssignment className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Purchase Bill</span>
                            </NavLink>
                          )}

                          {/* Inventory/Products */}
                          {isMenuAllowedByName("Inventory/Products") && (
                            <NavLink
                              to="/admin/inventory-management/products"
                              className={({ isActive }) =>
                                getNavLinkClass(
                                  isActive ||
                                    location.pathname.startsWith(
                                      "/admin/inventory-management/product-import",
                                    ) ||
                                    location.pathname.startsWith(
                                      "/admin/inventory-management/products",
                                    ),
                                  true,
                                )
                              }
                            >
                              <MdCategory className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Inventory/Products</span>
                            </NavLink>
                          )}

                          {/* Suppliers */}
                          {isMenuAllowedByName("Suppliers") && (
                            <NavLink
                              to="/admin/inventory-management/suppliers"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdLocalShipping className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Suppliers</span>
                            </NavLink>
                          )}

                          {/* Assigned Inventory */}
                          {isMenuAllowedByName("Assigned Inventory") && (
                            <NavLink
                              to="/admin/inventory-management/assigned-inventory"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdPeople className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Assigned Inventory</span>
                            </NavLink>
                          )}
                        </div>
                      )}
                    </li>
                  )}

                  {/* Staff Management - hidden for free subscription */}
                  {isMenuAllowedByName("Staff Management") &&
                    // !isFreePlan &&
                    (data?.user?.role_data?.name === "admin" ||
                      data?.user?.role_data?.name === "manager") && (
                      <li>
                        <NavLink
                          to="/admin/staff-management/employees"
                          className={({ isActive }) =>
                            getNavLinkClass(isActive, false)
                          }
                        >
                          <MdPeople className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          <span>Staff Management</span>
                        </NavLink>
                      </li>
                    )}

                  {/* Clients/Pets */}
                  {isMenuAllowedByName("Clients/Pets") && (
                    <li>
                      <button
                        type="button"
                        onClick={() => toggleMainMenu("clientsPets")}
                        className={getParentBtnClass(
                          openMenu.main === "clientsPets",
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <MdPets className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          <span>Clients/Pets</span>
                        </span>

                        <span>
                          {openMenu.main === "clientsPets" ? (
                            <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                          ) : (
                            <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                          )}
                        </span>
                      </button>

                      {openMenu.main === "clientsPets" && (
                        <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                          {/* Clients / Pets */}
                          <NavLink
                            to="/admin/clients-pets"
                            className={({ isActive }) =>
                              getNavLinkClass(isActive, true)
                            }
                          >
                            <MdPets className="text-lg transition-transform duration-200 group-hover:scale-110" />
                            <span>Clients/Pets</span>
                          </NavLink>

                          {/* New Customer Form */}
                          <NavLink
                            to="/admin/customer-onboarding-form"
                            className={({ isActive }) =>
                              getNavLinkClass(isActive, true)
                            }
                          >
                            <MdAssignment className="text-lg transition-transform duration-200 group-hover:scale-110" />
                            <span>Customer Onboarding Form</span>
                          </NavLink>
                        </div>
                      )}
                    </li>
                  )}

                  {/* Order Management - hidden for free subscription */}
                  {isMenuAllowedByName("Order Management") && (
                    // !isFreePlan &&
                    <li>
                      <button
                        onClick={() => toggleMainMenu("orderManagement")}
                        className={getParentBtnClass(
                          openMenu.main === "orderManagement",
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <MdShoppingCart className="text-lg transition-transform duration-200 group-hover:scale-110" />
                          Order Management
                        </span>
                        <span>
                          {openMenu.main === "orderManagement" ? (
                            <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                          ) : (
                            <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                          )}
                        </span>
                      </button>

                      {openMenu.main === "orderManagement" && (
                        <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                          {/* Orders */}
                          {isMenuAllowedByName("Orders") && (
                            <NavLink
                              to="/admin/order-management/orders"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdShoppingCart className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Orders</span>
                            </NavLink>
                          )}

                          {/* 
                          {isMenuAllowedByName("Invoice") && (
                            <ul className="ml-4 mt-1 space-y-2">
                              <li>
                                <NavLink
                                  to="/invoice"
                                  className={({ isActive }) =>
                                    `flex items-center px-3 py-2 gap-2 rounded transition ${
                                      isActive
                                        ? "bg-gray-700"
                                        : "hover:bg-gray-700"
                                    }`
                                  }
                                >
                                  <TbFileInvoice className="text-lg" />
                                  Invoice
                                </NavLink>
                              </li>
                            </ul>
                          )}
                          */}

                          {/* Payments Received */}
                          {isMenuAllowedByName("Payments Received") && (
                            <NavLink
                              to="/admin/order-management/payments"
                              className={({ isActive }) =>
                                getNavLinkClass(isActive, true)
                              }
                            >
                              <MdOutlinePayments className="text-lg transition-transform duration-200 group-hover:scale-110" />
                              <span>Payments Received</span>
                            </NavLink>
                          )}
                        </div>
                      )}
                    </li>
                  )}

                  {/* Expense Management - hidden for free subscription */}
                  {isMenuAllowedByName("Expense Management") &&
                    // !isFreePlan &&
                    data?.user?.role_data?.name === "admin" && (
                      <li>
                        <button
                          onClick={() => toggleMainMenu("expenseManagement")}
                          className={getParentBtnClass(
                            openMenu.main === "expenseManagement",
                          )}
                        >
                          <span className="flex items-center gap-2.5">
                            <BiLineChartDown className="text-lg transition-transform duration-200 group-hover:scale-110" />
                            Expense Management
                          </span>
                          <span>
                            {openMenu.main === "expenseManagement" ? (
                              <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                            ) : (
                              <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                            )}
                          </span>
                        </button>

                        {openMenu.main === "expenseManagement" && (
                          <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                            {/* Expenses */}
                            {isMenuAllowedByName("Expenses") && (
                              <NavLink
                                to="/admin/expense-management/expenses"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <BiLineChartDown className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Expenses</span>
                              </NavLink>
                            )}

                            {/* Payments */}
                            {isMenuAllowedByName("Payments") && (
                              <NavLink
                                to="/admin/expense-management/payments"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdOutlinePayments className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Payments</span>
                              </NavLink>
                            )}
                          </div>
                        )}
                      </li>
                    )}

                  {/* Reports - visible for admin only */}
                  {isMenuAllowedByName("Reports") &&
                    data?.user?.role_data?.name === "admin" && (
                      <li>
                        <button
                          onClick={() => toggleMainMenu("reports")}
                          className={getParentBtnClass(
                            openMenu.main === "reports",
                          )}
                        >
                          <span className="flex items-center gap-2.5">
                            <HiOutlineClipboardList className="text-lg transition-transform duration-200 group-hover:scale-110" />
                            Reports
                          </span>
                          <span>
                            {openMenu.main === "reports" ? (
                              <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                            ) : (
                              <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                            )}
                          </span>
                        </button>

                        {openMenu.main === "reports" && (
                          <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                            {/* Standard Reports */}
                            {isMenuAllowedByName("Standard Reports") && (
                              <NavLink
                                to="/admin/reports/standard"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdAssignment className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Standard Reports</span>
                              </NavLink>
                            )}

                            {/* feedback */}
                            {isMenuAllowedByName("Feedback") && (
                              <NavLink
                                to="/admin/reports/feedback"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdFeedback className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Feedback</span>
                              </NavLink>
                            )}
                            {isMenuAllowedByName("Reminder History") && (
                              <NavLink
                                to="/admin/reports/reminderhistory"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdNotificationsNone className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>Reminder History</span>
                              </NavLink>
                            )}
                          </div>
                        )}
                      </li>
                    )}
                  {/* forms  */}
                  {isMenuAllowedByName("Forms") && (
                    <li>
                      <NavLink
                        to="/admin/forms"
                        className={({ isActive }) =>
                          getNavLinkClass(isActive, false)
                        }
                      >
                        <MdAssignment className="text-lg transition-transform duration-200 group-hover:scale-110" />
                        <span>Forms</span>
                      </NavLink>
                    </li>
                  )}

                  {/* Room Management */}
                  {isMenuAllowedByName("Room Management") && boardingActive && (
                    <li>
                      <NavLink
                        to="/admin/room_management"
                        className={({ isActive }) =>
                          getNavLinkClass(isActive, false)
                        }
                      >
                        <BsHouseGearFill className="text-lg transition-transform duration-200 group-hover:scale-110" />
                        <span>Room Management</span>
                      </NavLink>
                    </li>
                  )}

                  {/* Promotion & Discounts */}
                  <li>
                    <NavLink
                      to="/admin/discounts"
                      className={({ isActive }) =>
                        getNavLinkClass(isActive, false)
                      }
                    >
                      <MdLocalOffer className="text-lg transition-transform duration-200 group-hover:scale-110" />
                      <span>Promotion/Discounts</span>
                    </NavLink>
                  </li>

                  {/* Settings */}
                  {isMenuAllowedByName("Settings") &&
                    data?.user?.role_data?.name === "admin" && (
                      <li>
                        <button
                          onClick={() => toggleMainMenu("settings")}
                          className={getParentBtnClass(
                            openMenu.main === "settings",
                          )}
                        >
                          <span className="flex items-center gap-2.5">
                            <MdSettings className="text-lg transition-transform duration-200 group-hover:scale-110" />
                            Settings
                          </span>
                          <span>
                            {openMenu.main === "settings" ? (
                              <MdKeyboardArrowDown className="text-lg transition-transform duration-200" />
                            ) : (
                              <MdKeyboardArrowRight className="text-lg transition-transform duration-200" />
                            )}
                          </span>
                        </button>

                        {openMenu.main === "settings" && (
                          <div className="ml-3.5 mt-1 space-y-1 border-l border-gray-200/70 pl-2">
                            {/* Online Profile */}
                            {isMenuAllowedByName("Profile Settings") && (
                              <NavLink
                                to="/admin/settings/general_settings"
                                className={({ isActive }) =>
                                  getNavLinkClass(isActive, true)
                                }
                              >
                                <MdPerson className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                <span>General Settings</span>
                              </NavLink>
                            )}
                            {/* online profile */}
                            {isMenuAllowedByName("Online Profile") &&
                              !isShopOnly && (
                                <NavLink
                                  to="/admin/settings/online_profile"
                                  className={({ isActive }) =>
                                    getNavLinkClass(isActive, true)
                                  }
                                >
                                  <MdPerson className="text-lg transition-transform duration-200 group-hover:scale-110" />
                                  <span>Online Profile</span>
                                </NavLink>
                              )}

                            {/* Size Management */}
                            {/* {isMenuAllowedByName("Size Management") && (
                              <ul className="ml-4 mt-1 space-y-2">
                                <li>
                                  <NavLink
                                    to="/admin/settings/size_management"
                                    className={({ isActive }) =>
                                      `flex items-center px-3 py-2 gap-2 transition rounded ${
                                        isActive
                                          ? "bg-gray-700"
                                          : "hover:bg-gray-700"
                                      }`
                                    }
                                  >
                                    <RiCustomSize className="text-lg" />
                                    Size Management
                                  </NavLink>
                                </li>
                              </ul>
                            )} */}

                            {/* Coat Management */}
                            {/* {isMenuAllowedByName("Coat Management") &&
                              groomingActive && (
                                <ul className="ml-4 mt-1 space-y-2">c
                                  <li>
                                    <NavLink
                                      to="/admin/settings/coat_management"
                                      className={({ isActive }) =>
                                        `flex items-center px-3 py-2 gap-2 transition rounded ${
                                          isActive
                                            ? "bg-gray-700"
                                            : "hover:bg-gray-700"
                                        }`
                                      }
                                    >
                                      <MdOutlinePets className="text-lg" />
                                      Coat Management
                                    </NavLink>
                                  </li>
                                </ul>
                              )} */}

                            {/* Promotions & Coupons */}
                            {/* <ul className="ml-4 mt-1 space-y-2">
                              <li>
                                <NavLink
                                  to="/admin/settings/promotions_and_coupons"
                                  className={({ isActive }) =>
                                    `flex items-center px-3 py-2 gap-2 rounded transition ${
                                      isActive
                                        ? "bg-gray-700"
                                        : "hover:bg-gray-700"
                                    }`
                                  }
                                >
                                  <MdLocalOffer className="text-lg" />
                                  Promotions/Coupons
                                </NavLink>
                              </li>
                            </ul> */}

                            {/* <ul className="ml-4 mt-1 space-y-2">
                              <li>
                                <NavLink
                                  to="/admin/settings/forms"
                                  className={({ isActive }) =>
                                    `flex items-center px-3 py-2 gap-2 rounded transition ${
                                      isActive
                                        ? "bg-gray-700"
                                        : "hover:bg-gray-700"
                                    }`
                                  }
                                >
                                  <MdAssignment className="text-lg" />
                                  Forms
                                </NavLink>
                              </li>
                            </ul> */}

                            {/* Store Management 
                            {isMenuAllowedByName("Store Management") &&
                              petStoreActive && (
                                <ul className="ml-4 mt-1 space-y-2">
                                  <li>
                                    <NavLink
                                      to="/admin/settings/store_management"
                                      className={({ isActive }) =>
                                        `flex items-center px-3 py-2 gap-2 rounded transition ${isActive
                                          ? "bg-gray-700"
                                          : "hover:bg-gray-700"
                                        }`
                                      }
                                    >
                                      <BsShopWindow className="text-lg" />
                                      Store Management
                                    </NavLink>
                                  </li>
                                </ul>
                              )}*/}

                            {/* Service Amenities */}
                            {/* {isMenuAllowedByName("Service Amenities") &&
                              (groomingActive || boardingActive) && (
                                <ul className="ml-4 mt-1 space-y-2">
                                  <li>
                                    <NavLink
                                      to="/admin/settings/service_amenities"
                                      className={({ isActive }) =>
                                        `flex items-center px-3 py-2 gap-2 rounded transition ${
                                          isActive
                                            ? "bg-gray-700"
                                            : "hover:bg-gray-700"
                                        }`
                                      }
                                    >
                                      <MdAddBox className="text-lg" />
                                      Service Amenities
                                    </NavLink>
                                  </li>
                                </ul>
                              )} */}
                          </div>
                        )}
                      </li>
                    )}
                </ul>
              </aside>
            )}
            {/* Main Content */}
            {/* <main
              className={`${
                showSidebar ? "ml-[16vw] h-[100vh] w-[84vw]" : "w-full h-full overflow-hidden"
              }`}
            >
              <>
                <Breadcrumbs />
              </>
              {children}
            </main> */}
            <main
              className={
                noPageScroll
                  ? `h-[calc(100vh-70px)] overflow-hidden flex flex-col transition-all duration-300 ease-in-out ${
                      isEmployee
                        ? "ml-0 w-full"
                        : "ml-0 xl:ml-[max(16vw,240px)] w-full xl:w-[calc(100%-max(16vw,240px))]"
                    }`
                  : `h-full transition-all duration-300 ease-in-out ${
                      isEmployee
                        ? "ml-0 w-full"
                        : "ml-0 xl:ml-[max(16vw,240px)] w-full xl:w-[calc(100%-max(16vw,240px))]"
                    }`
              }
            >
              <Breadcrumbs />
              {noPageScroll ? (
                <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
              ) : (
                children
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};;

export default ThemeMaker;
